// SCR-014: a customer's linked AWS accounts (the installer's "Linked AWS accounts" tab). Link
// account → a one-time card with the quick-create link and the ExternalId (shown once). An
// account's row opens its summary: Verify assumes the bootstrap role; Unlink, confirmed by the
// typed account id, while nothing uses the link.
import { useContext, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import type { ScreenProps } from "../../router/route";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { RouterContext } from "../../router/router";
import { ConfirmDialog, Dialog } from "@nexus/ui";
import { RowDetail } from "@nexus/ui";
import { Checkbox, Field, Fieldset, errorId } from "@nexus/ui";
import { CustomerPicker, useCustomerName } from "../../ui/customer-directory";
import { Card, Chip, FilterBar } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import { useBreadcrumb } from "../../ui/shell";
import { DataTable, type Column } from "@nexus/ui";
import { InstallerDisabled } from "./disabled";
import { InstallerHeader } from "./header";
import {
  accountRow,
  ALL_CAPABILITIES,
  assumeRoleFailureMessage,
  capabilityLabel,
  emptyLinkAccountDraft,
  installerAccountsPath,
  isInstallerDisabled,
  linkAccountRequest,
  linkCreatedView,
  toggleCapability,
  validateLinkAccount,
  verifyFailureMessage,
  type AccountRow,
  type AwsAccountLinkCreated,
  type AwsCapability,
  type LinkAccountDraft,
  type LinkCreatedView,
} from "./model";

type Params = { orgId: string };

interface ListState {
  loading: boolean;
  error: ApiError | null;
  rows: AccountRow[];
  nextCursor: string | null;
}

const emptyList: ListState = { loading: true, error: null, rows: [], nextCursor: null };

const CAPABILITY_HELP = {
  discover: "installer.capability.discover",
  deploy: "installer.capability.deploy",
  terraform: "installer.capability.terraform",
  cur: "installer.capability.cur",
} as const satisfies Record<AwsCapability, string>;

function day(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function AwsAccounts({ params }: ScreenProps<"/installer/accounts/:orgId">) {
  return <AwsAccountsScreen orgId={(params as Params).orgId} />;
}

function AwsAccountsScreen({ orgId }: { orgId: string }) {
  const api = useApi();
  const router = useContext(RouterContext);
  const name = useCustomerName(orgId);
  const [list, setList] = useState<ListState>(emptyList);
  const [linking, setLinking] = useState(false);
  const [created, setCreated] = useState<LinkCreatedView | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<ApiError | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState<AccountRow | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useBreadcrumb([{ label: "Linked AWS accounts" }, { label: name }]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setList((current) => ({ ...current, loading: true, error: null }));
    api
      .get("/v1/admin/orgs/{orgId}/aws-accounts", { path: { orgId } })
      .then((page) => {
        if (cancelled) return;
        setList({ loading: false, error: null, rows: page.items.map(accountRow), nextCursor: page.nextCursor });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setList({ loading: false, error, rows: [], nextCursor: null });
        else throw error;
      });
    return () => {
      cancelled = true;
    };
  }, [api, orgId, reloadToken]);

  async function verify(id: string) {
    if (!api) return;
    setBusyId(id);
    setRowError(null);
    try {
      const link = await api.post("/v1/admin/orgs/{orgId}/aws-accounts/{linkId}/verify", { path: { orgId, linkId: id } });
      setList((current) => ({ ...current, rows: current.rows.map((r) => (r.id === id ? accountRow(link) : r)) }));
    } catch (error) {
      if (error instanceof ApiError) setRowError(error);
      else throw error;
    } finally {
      setBusyId(null);
    }
  }

  // A refused unlink reopens the account's summary, which shows why.
  async function unlink(id: string) {
    if (!api) return;
    setBusyId(id);
    setRowError(null);
    try {
      await api.delete("/v1/admin/orgs/{orgId}/aws-accounts/{linkId}", { path: { orgId, linkId: id } });
      setList((current) => ({ ...current, rows: current.rows.filter((r) => r.id !== id) }));
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setSelectedId(id);
      setRowError(error);
    } finally {
      setBusyId(null);
      setUnlinking(null);
    }
  }

  const selected = list.rows.find((row) => row.id === selectedId) ?? null;
  const verifiedChip = (row: AccountRow) =>
    row.verified ? (
      <Chip tone="green" {...(row.verifiedAt ? { title: row.verifiedAt } : {})}>
        Verified {day(row.verifiedAt)}
      </Chip>
    ) : (
      <Chip tone="amber">Not verified</Chip>
    );

  const columns: readonly Column<AccountRow>[] = [
    { key: "awsAccountId", header: "AWS account", cell: (row) => <span class="mono">{row.awsAccountId}</span> },
    {
      key: "capabilities",
      header: "Capabilities",
      help: "installer.capabilities",
      cell: (row) => row.capabilities.map(capabilityLabel).join(", "),
    },
    { key: "templateVersion", header: "Template version", help: "installer.templateVersion", cell: (row) => row.templateVersion ?? "—" },
    {
      key: "verified",
      header: "Verified",
      help: "installer.verified",
      cell: verifiedChip,
    },
  ];

  if (!api) return <p role="alert">Admin API not available.</p>;

  if (isInstallerDisabled(list.error)) return <InstallerDisabled />;

  return (
    <div class="page aws-accounts">
      <InstallerHeader tab="accounts" orgId={orgId} />
      <FilterBar>
        <Field id="accounts-customer" label="Customer" help="common.customer" compact>
          <CustomerPicker
            id="accounts-customer"
            value={orgId}
            onChange={(next) => {
              if (next && next !== orgId && router) router.navigate(installerAccountsPath(next));
            }}
          />
        </Field>
      </FilterBar>
      {created && <LinkCreatedCard view={created} onClose={() => setCreated(null)} />}
      {rowError && !selected && (
        <div class="banner banner-error" role="alert">
          <p>
            <code class="error-code">{rowError.code}</code> {verifyFailureMessage(rowError)}
          </p>
        </div>
      )}
      <Card
        title={`${name}'s linked AWS accounts`}
        titleId="linked-accounts-heading"
        help="installer.linkedAccounts"
        actions={
          <button type="button" class="button button-primary" onClick={() => setLinking(true)}>
            <Icon name="plus" />
            Link account
          </button>
        }
      >
        <DataTable
          caption={`${name}'s linked AWS accounts`}
          hideCaption
          columns={columns}
          rows={list.rows}
          rowKey={(row) => row.id}
          loading={list.loading}
          error={list.error}
          onRetry={() => setReloadToken((n) => n + 1)}
          emptyMessage={`${name} has no linked AWS account yet. Link one to install the proxy there.`}
          onRowClick={(row) => (setRowError(null), setSelectedId(row.id))}
          rowLabel={(row) => `AWS account ${row.awsAccountId}`}
        />
      </Card>
      {selected && (
        <RowDetail
          title={`AWS account ${selected.awsAccountId}`}
          open
          onClose={() => setSelectedId(null)}
          facts={[
            { label: "Capabilities", value: selected.capabilities.map(capabilityLabel).join(", ") },
            { label: "Template version", value: selected.templateVersion ?? "—" },
            { label: "Verified", value: verifiedChip(selected) },
          ]}
          actions={
            <>
              <button type="button" class="button button-danger" disabled={busyId === selected.id} onClick={() => (setSelectedId(null), setUnlinking(selected))}>
                Unlink
              </button>
              <button type="button" class="button button-primary" disabled={busyId === selected.id} onClick={() => void verify(selected.id)}>
                {busyId === selected.id ? "Verifying…" : "Verify"}
              </button>
            </>
          }
        >
          {rowError && (
            <p role="alert" class="banner banner-error">
              <code class="error-code">{rowError.code}</code> {verifyFailureMessage(rowError)}
            </p>
          )}
        </RowDetail>
      )}
      <ConfirmDialog
        title="Unlink AWS account"
        open={unlinking !== null}
        message={unlinking ? `Unlinking AWS account ${unlinking.awsAccountId} removes the link: Nexus no longer assumes its roles there.` : ""}
        confirmLabel="Unlink"
        typedName={unlinking?.awsAccountId ?? ""}
        busy={busyId === unlinking?.id}
        onConfirm={() => unlinking && void unlink(unlinking.id)}
        onClose={() => setUnlinking(null)}
      />
      <LinkAccountDialog
        open={linking}
        orgId={orgId}
        customerName={name}
        onClose={() => setLinking(false)}
        onCreated={(link) => {
          setLinking(false);
          setCreated(linkCreatedView(link));
          setReloadToken((n) => n + 1);
        }}
      />
    </div>
  );
}

function LinkAccountDialog({
  open,
  orgId,
  customerName,
  onClose,
  onCreated,
}: {
  open: boolean;
  orgId: string;
  customerName: string;
  onClose(): void;
  onCreated(link: AwsAccountLinkCreated): void;
}) {
  const api = useApi();
  const [draft, setDraft] = useState<LinkAccountDraft>(emptyLinkAccountDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const errors = validateLinkAccount(draft);
  const accountError = draft.awsAccountId !== "" ? errors.awsAccountId : undefined;

  async function submit(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!api || Object.keys(errors).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const link = await api.post("/v1/admin/orgs/{orgId}/aws-accounts", { path: { orgId }, body: linkAccountRequest(draft) });
      setDraft(emptyLinkAccountDraft);
      onCreated(link);
    } catch (e) {
      if (e instanceof ApiError) setError(e);
      else throw e;
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Link an AWS account"
      open={open}
      onClose={onClose}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="link-account-form" class="button button-primary" disabled={busy || Object.keys(errors).length > 0}>
            {busy ? "Linking…" : "Link account"}
          </button>
        </>
      }
    >
      <form id="link-account-form" class="stack" onSubmit={(event) => void submit(event)}>
        <p class="muted">
          Links one of {customerName}'s AWS accounts. You get a quick-create link for the customer's bootstrap stack and its
          ExternalId, shown once.
        </p>
        {error && (
          <p role="alert" class="banner banner-error">
            {assumeRoleFailureMessage(error)}
          </p>
        )}
        <Field id="link-account-id" label="AWS account id" help="installer.awsAccountId" error={accountError} hint="12 digits.">
          <input
            id="link-account-id"
            type="text"
            inputMode="numeric"
            class="input-mono"
            autocomplete="off"
            value={draft.awsAccountId}
            aria-invalid={accountError ? "true" : undefined}
            aria-describedby={accountError ? errorId("link-account-id") : undefined}
            onInput={(event) => setDraft({ ...draft, awsAccountId: event.currentTarget.value })}
          />
        </Field>
        <Fieldset label="Capabilities" help="installer.capabilities" error={draft.awsAccountId !== "" ? errors.capabilities : undefined}>
          {ALL_CAPABILITIES.map((capability) => (
            <Checkbox
              key={capability}
              id={`link-capability-${capability}`}
              label={capabilityLabel(capability)}
              help={CAPABILITY_HELP[capability]}
              checked={draft.capabilities.includes(capability)}
              onChange={() => setDraft({ ...draft, capabilities: toggleCapability(draft.capabilities, capability) })}
            />
          ))}
        </Fieldset>
      </form>
    </Dialog>
  );
}

function LinkCreatedCard({ view, onClose }: { view: LinkCreatedView; onClose(): void }) {
  return (
    <Card title={`Account linked — ${view.awsAccountId}`} titleId="link-created-heading" class="link-created-card">
      <p role="alert" class="banner banner-warning">
        {view.warning}
      </p>
      <p>{view.instructions}</p>
      <p>
        <a class="button button-secondary" href={view.quickCreateUrl} target="_blank" rel="noreferrer">
          <Icon name="external" />
          Open the quick-create link
        </a>
      </p>
      <Field id="external-id" label="ExternalId" help="installer.externalId">
        <span class="row">
          <input
            id="external-id"
            type="text"
            class="input-mono external-id-input"
            readOnly
            value={view.externalId}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" class="button button-secondary" onClick={() => void navigator.clipboard?.writeText(view.externalId)}>
            <Icon name="copy" />
            Copy
          </button>
        </span>
      </Field>
      <div class="form-actions form-actions-start">
        <button type="button" class="button button-primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Card>
  );
}
